import React from 'react';
import { Filter, Globe, Users, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { ArenaType } from './ArenaTypeBadge';

export interface ArenaFilters {
  language: string | null;
  arenaType: ArenaType | null;
  participantRange: string | null;
}

interface ArenaFiltersProps {
  filters: ArenaFilters;
  onFiltersChange: (filters: ArenaFilters) => void;
  activeFilterCount: number;
}

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
];

const ARENA_TYPES: { value: ArenaType; label: string }[] = [
  { value: 'vibe_booth', label: '💬 Vibe Booth' },
  { value: 'dating_arena', label: '💘 Dating Arena' },
  { value: 'global_arena', label: '🌍 Global Arena' },
  { value: 'after_hours', label: '🕯️ After Hours' },
  { value: 'event_arena', label: '🎉 Event Arena' },
];

const PARTICIPANT_RANGES = [
  { value: '1-10', label: '1-10 people' },
  { value: '10-25', label: '10-25 people' },
  { value: '25-50', label: '25-50 people' },
  { value: '50+', label: '50+ people' },
];

export const ArenaDiscoveryFilters: React.FC<ArenaFiltersProps> = ({
  filters,
  onFiltersChange,
  activeFilterCount,
}) => {
  const clearFilters = () => {
    onFiltersChange({
      language: null,
      arenaType: null,
      participantRange: null,
    });
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full bg-white/50 relative"
        >
          <Filter className="w-4 h-4 mr-1" />
          Filters
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto max-h-[70vh] rounded-t-3xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filter Arenas
            </span>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 pb-6">
          {/* Language Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Language
            </Label>
            <Select
              value={filters.language || 'all'}
              onValueChange={(value) =>
                onFiltersChange({ ...filters, language: value === 'all' ? null : value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All languages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All languages</SelectItem>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Arena Type Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4" />
              Arena Type
            </Label>
            <Select
              value={filters.arenaType || 'all'}
              onValueChange={(value) =>
                onFiltersChange({
                  ...filters,
                  arenaType: value === 'all' ? null : (value as ArenaType),
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {ARENA_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Participant Count Filter */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Participant Count
            </Label>
            <Select
              value={filters.participantRange || 'all'}
              onValueChange={(value) =>
                onFiltersChange({
                  ...filters,
                  participantRange: value === 'all' ? null : value,
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Any size" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any size</SelectItem>
                {PARTICIPANT_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {range.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const parseParticipantRange = (range: string | null): { min: number; max: number } | null => {
  if (!range) return null;
  
  switch (range) {
    case '1-10':
      return { min: 1, max: 10 };
    case '10-25':
      return { min: 10, max: 25 };
    case '25-50':
      return { min: 25, max: 50 };
    case '50+':
      return { min: 50, max: Infinity };
    default:
      return null;
  }
};

export default ArenaDiscoveryFilters;
